import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import api from '../services/api'
import authService from '../services/authService'

// ── Auth Context ──────────────────────────────────────────────────────────
const AuthContext = createContext(null)

// Impersonation state can't be trusted from local/component state alone -
// a background tab can end a session, or the backend TTL can expire it,
// while this tab's banner still thinks it's active. Every load re-derives
// it from /auth/me, and api.js dispatches this event on any 403 so a
// scope-mismatch response triggers an immediate reconciliation instead of
// waiting for the next poll.
const SCOPE_MISMATCH_EVENT = 'auth:scope-mismatch'
const IMPERSONATION_POLL_MS = 60_000

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cen_user')) }
    catch { return null }
  })
  const [loading, setLoading] = useState(false)
  const [impersonation, setImpersonation] = useState(null)

  useEffect(() => {
    Sentry.setUser(user ? { id: user.id, email: user.email } : null)
  }, [user])

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me')
      const nextUser = data.user || data
      setUser(nextUser)
      setImpersonation(nextUser?.impersonation || null)
      localStorage.setItem('cen_user', JSON.stringify(nextUser))
      return nextUser
    } catch {
      localStorage.removeItem('cen_user')
      setUser(null)
      setImpersonation(null)
      return null
    }
  }, [])

  useEffect(() => {
    let cached = null
    try {
      cached = JSON.parse(localStorage.getItem('cen_user') ?? 'null')
    } catch {
      localStorage.removeItem('cen_user')
    }
    if (!cached) return

    refreshMe()
  }, [refreshMe])

  useEffect(() => {
    window.addEventListener(SCOPE_MISMATCH_EVENT, refreshMe)
    return () => window.removeEventListener(SCOPE_MISMATCH_EVENT, refreshMe)
  }, [refreshMe])

  useEffect(() => {
    if (!impersonation?.active) return
    const id = setInterval(refreshMe, IMPERSONATION_POLL_MS)
    return () => clearInterval(id)
  }, [impersonation?.active, refreshMe])

  const login = useCallback(async (username, password) => {
    setLoading(true)
    try {
      const user = await authService.login(username, password)

      // Persistencia
      localStorage.setItem('cen_user', JSON.stringify(user))

      setUser(user)
      setImpersonation(user?.impersonation || null)
      return user
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    setUser(null)
    setImpersonation(null)
  }, [])

  const startImpersonation = useCallback(async (companyId) => {
    await authService.impersonate(companyId)
    return refreshMe()
  }, [refreshMe])

  const endImpersonationSession = useCallback(async () => {
    await authService.endImpersonation()
    return refreshMe()
  }, [refreshMe])

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      // isAdmin stays tied to the real account always - it gates actual
      // capability (Companies/Users/Campaigns/Buyers CRUD elsewhere), and
      // impersonation is a scope override, never a credential swap (see
      // companies/impersonation.py on the backend - request.user never
      // changes). isPartner, by contrast, only ever drives UI routing
      // (which sidebar, which "/" redirect) - safe, and part of the point
      // of impersonation, to also flip true while actively impersonating,
      // so the admin sees the same nav a real partner would.
      isAdmin: user?.is_superuser || user?.is_staff,
      isPartner: (!user?.is_superuser && !user?.is_staff && !!user?.company_id) || !!impersonation?.active,
      mustChangePassword: !!user?.must_change_password,
      impersonation,
      startImpersonation,
      endImpersonationSession,
      refreshMe,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

// ── Theme Context ─────────────────────────────────────────────────────────
const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('cen_theme') || 'light')

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('cen_theme', theme)
  }, [theme])

  const toggle = () => setTheme(t => t === 'light' ? 'dark' : 'light')

  return (
    <ThemeContext.Provider value={{ theme, toggle, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
